import { useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
    Sparkles, MessageCircle, Send, X, ChevronRight, MapPin, Phone,
    Clock, Check, User, Mail, CalendarDays,
} from "lucide-react";
import { toast } from "sonner";
import BrandLogo from "@/components/BrandLogo";

const HERO_IMG =
    "https://www.aestheticssalon.co.uk/wp-content/uploads/2020/07/shutterstock_1505205299.jpg";
const AI_AVATAR = "/ai-avatar.png";
const ANY_STAFF = "__any__";

const EYEBROW = "font-eyebrow text-[11px] font-semibold uppercase tracking-[0.18em] sm:text-xs";
const fieldClass =
    "h-11 border-[#E8E4DE] bg-[#FFFEF9] text-sm font-medium text-[#2D2926] shadow-sm placeholder:text-[#9A9088] focus-visible:border-[#D4AF37]/55 focus-visible:ring-[#D4AF37]/20";
const labelClass = "text-[11px] font-bold uppercase tracking-[0.12em] text-[#3D2817]";
const primaryBtn =
    "bg-[#4A0E17] font-semibold shadow-[0_4px_14px_rgba(74,14,23,0.25)] hover:bg-[#3a0b12]";
const navBtn =
    "border-[#E8E4DE] bg-white text-[#3D2817] shadow-sm hover:border-[#D4AF37]/45 hover:bg-[#FFFEF9]";

const inr = (n) => `₹${(n || 0).toLocaleString("en-IN")}`;
const STEP_LABELS = ["Choose service", "Pick a time", "Your details"];

function GoldDivider({ className = "max-w-[160px]" }) {
    return (
        <div className={`flex items-center ${className}`}>
            <div className="h-px flex-1 bg-gradient-to-r from-transparent via-[#D4AF37]/80 to-transparent" />
            <span className="px-2 text-[10px] leading-none text-[#C9A227]">◆</span>
            <div className="h-px flex-1 bg-gradient-to-r from-transparent via-[#D4AF37]/80 to-transparent" />
        </div>
    );
}

function SalonCard({ children, className = "" }) {
    return (
        <div className={`relative overflow-hidden rounded-xl border border-[#E8E4DE] bg-white shadow-[0_4px_22px_rgba(45,41,38,0.05)] ${className}`}>
            <div className="absolute inset-x-0 top-0 h-[2px] bg-[#4A0E17]/85" />
            {children}
        </div>
    );
}

function formatSlotTime(iso) {
    try {
        return new Date(iso).toLocaleTimeString("en-IN", {
            hour: "2-digit", minute: "2-digit", hour12: false,
        });
    } catch {
        return "";
    }
}

function formatDateTime(iso) {
    try {
        return new Date(iso).toLocaleString("en-IN", {
            weekday: "short", day: "numeric", month: "short",
            hour: "2-digit", minute: "2-digit",
        });
    } catch {
        return iso;
    }
}

export default function PublicBooking() {
    const { slug } = useParams();
    const [tenant, setTenant] = useState(null);
    const [outlet, setOutlet] = useState(null);
    const [services, setServices] = useState([]);
    const [categories, setCategories] = useState([]);
    const [staff, setStaff] = useState([]);
    const [loadErr, setLoadErr] = useState(false);

    const [step, setStep] = useState(1);
    const [activeCat, setActiveCat] = useState(null);
    const [selectedService, setSelectedService] = useState(null);
    const [selectedStaff, setSelectedStaff] = useState("");
    const [selectedSlot, setSelectedSlot] = useState("");
    const [date, setDate] = useState(() => new Date().toISOString().slice(0, 10));
    const [slots, setSlots] = useState([]);
    const [slotsLoading, setSlotsLoading] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [form, setForm] = useState({ customer_name: "", mobile: "", email: "", notes: "" });
    const [confirmation, setConfirmation] = useState(null);

    useEffect(() => {
        setLoadErr(false);
        api.get(`/public/tenant/${slug}`)
            .then((r) => {
                setTenant(r.data.tenant);
                setOutlet(r.data.outlets?.[0] || null);
                setServices(r.data.services || []);
                setCategories(r.data.categories || []);
                setStaff(r.data.staff || []);
                setActiveCat(r.data.categories?.[0]?.id ?? null);
            })
            .catch(() => {
                setLoadErr(true);
                toast.error("Salon not found");
            });
    }, [slug]);

    useEffect(() => {
        if (!selectedService || !date) {
            setSlots([]);
            return;
        }
        setSlotsLoading(true);
        setSelectedSlot("");
        api.get(`/public/availability/${slug}`, {
            params: {
                date,
                service_id: selectedService.id,
                staff_id: selectedStaff || undefined,
            },
        })
            .then((r) => setSlots(r.data.slots || []))
            .catch(() => {
                setSlots([]);
                toast.error("Could not load time slots");
            })
            .finally(() => setSlotsLoading(false));
    }, [selectedService, date, selectedStaff, slug]);

    const filteredServices = useMemo(() => {
        if (!activeCat) return services;
        return services.filter((s) => s.category_id === activeCat);
    }, [services, activeCat]);

    const submitBooking = async () => {
        if (!form.customer_name.trim() || !form.mobile.trim() || !selectedSlot) {
            toast.error("Please fill name, mobile, and pick a time slot");
            return;
        }
        setSubmitting(true);
        try {
            const r = await api.post("/public/book", {
                tenant_slug: slug,
                customer_name: form.customer_name.trim(),
                mobile: form.mobile.trim(),
                email: form.email.trim() || undefined,
                service_id: selectedService.id,
                staff_id: selectedStaff || undefined,
                start_at: selectedSlot,
                notes: form.notes.trim() || "",
            });
            setConfirmation(r.data);
            setStep(4);
            toast.success("Booking confirmed!");
        } catch (err) {
            const msg = err.response?.data?.detail;
            toast.error(typeof msg === "string" ? msg : "Booking failed — try another slot");
        } finally {
            setSubmitting(false);
        }
    };

    if (loadErr) {
        return (
            <div className="flex min-h-screen items-center justify-center bg-[#FAF9F6] px-6 font-['Outfit',system-ui,sans-serif]">
                <div className="max-w-md text-center">
                    <h1 className="font-serif text-2xl font-semibold text-[#4A0E17]">Salon not found</h1>
                    <p className="mt-2 text-sm font-medium text-[#8A8178]">
                        This booking link may be invalid or the salon is no longer active.
                    </p>
                </div>
            </div>
        );
    }

    if (!tenant) {
        return (
            <div className="flex min-h-screen items-center justify-center bg-[#FAF9F6] font-['Outfit',system-ui,sans-serif]">
                <p className="font-serif text-xl text-[#4A0E17]">Loading your salon…</p>
            </div>
        );
    }

    const phone = outlet?.phone || "";
    const address = [outlet?.address, outlet?.city].filter(Boolean).join(", ");

    return (
        <div className="min-h-screen bg-[#FAF9F6] font-['Outfit',system-ui,sans-serif]">
            {/* Hero */}
            <header className="relative overflow-hidden border-b border-[#E8E4DE]/80">
                <div className="absolute inset-0">
                    <img src={HERO_IMG} alt="" className="h-full w-full object-cover object-[65%_center]" />
                    <div
                        className="absolute inset-0"
                        style={{
                            background:
                                "linear-gradient(105deg, rgba(74,14,23,0.88) 0%, rgba(74,14,23,0.72) 45%, rgba(45,8,15,0.55) 100%)",
                        }}
                    />
                </div>
                <div className="relative mx-auto max-w-5xl px-5 py-12 sm:px-8 sm:py-16">
                    <p className={`${EYEBROW} text-[#FFE9A8]`}>Book online</p>
                    <h1 className="mt-2 font-serif text-4xl font-semibold tracking-tight text-white sm:text-5xl">
                        {tenant.name}
                    </h1>
                    <GoldDivider className="mt-4" />
                    <div className="mt-5 flex flex-wrap gap-4 text-sm font-medium text-white/80">
                        {tenant.city && (
                            <span className="flex items-center gap-1.5">
                                <MapPin className="size-4 text-[#D4AF37]" />
                                {address || tenant.city}
                            </span>
                        )}
                        {phone && (
                            <span className="flex items-center gap-1.5">
                                <Phone className="size-4 text-[#D4AF37]" />
                                {phone}
                            </span>
                        )}
                        <span className="flex items-center gap-1.5">
                            <Clock className="size-4 text-[#D4AF37]" />
                            Open 10:00 – 20:00
                        </span>
                    </div>
                </div>
            </header>

            <main className="mx-auto max-w-5xl px-5 py-8 pb-28 sm:px-8 sm:py-10">
                {step < 4 && (
                    <div className="mb-8 flex flex-wrap items-center gap-3">
                        {[1, 2, 3].map((n) => (
                            <div key={n} className="flex items-center gap-2">
                                <span
                                    className={`flex size-8 items-center justify-center rounded-full text-xs font-bold transition-all ${
                                        step >= n
                                            ? "bg-[#4A0E17] text-white shadow-[0_2px_10px_rgba(74,14,23,0.3)]"
                                            : "border border-[#E8E4DE] bg-white text-[#8A8178]"
                                    }`}
                                >
                                    {step > n ? <Check className="size-4" /> : n}
                                </span>
                                {n < 3 && (
                                    <ChevronRight className="size-4 text-[#D4AF37]/70" />
                                )}
                            </div>
                        ))}
                        <span className="text-sm font-semibold text-[#8A8178]">
                            {STEP_LABELS[step - 1]}
                        </span>
                    </div>
                )}

                {/* Step 1 — services */}
                {step === 1 && (
                    <div className="grid gap-8 md:grid-cols-12" data-testid="booking-step-1">
                        {categories.length > 0 && (
                            <aside className="md:col-span-3">
                                <p className={EYEBROW} style={{ color: "#C9A227" }}>Categories</p>
                                <ul className="mt-3 space-y-1">
                                    {categories.map((c) => (
                                        <li key={c.id}>
                                            <button
                                                type="button"
                                                onClick={() => setActiveCat(c.id)}
                                                className={`flex w-full items-center gap-2.5 rounded-lg px-3 py-2.5 text-left text-sm font-semibold transition-all ${
                                                    activeCat === c.id
                                                        ? "bg-[#4A0E17] text-white shadow-sm"
                                                        : "text-[#3D2817] hover:bg-white hover:shadow-sm"
                                                }`}
                                                data-testid={`category-${c.id}`}
                                            >
                                                <span
                                                    className="size-2 shrink-0 rounded-full"
                                                    style={{ background: c.color || "#D4AF37" }}
                                                />
                                                {c.name}
                                            </button>
                                        </li>
                                    ))}
                                </ul>
                            </aside>
                        )}

                        <div className={categories.length > 0 ? "md:col-span-9" : "md:col-span-12"}>
                            <div className="grid gap-3 sm:grid-cols-2">
                                {filteredServices.map((s) => (
                                    <button
                                        key={s.id}
                                        type="button"
                                        onClick={() => {
                                            setSelectedService(s);
                                            setSelectedStaff("");
                                            setSelectedSlot("");
                                            setStep(2);
                                        }}
                                        className="group text-left transition-transform duration-200 hover:-translate-y-0.5"
                                        data-testid={`service-${s.id}`}
                                    >
                                        <SalonCard className="h-full p-5 transition-shadow group-hover:shadow-[0_8px_28px_rgba(45,41,38,0.08)]">
                                            <div className="flex items-start justify-between gap-3">
                                                <div className="min-w-0 flex-1">
                                                    <h4 className="font-serif text-xl font-semibold text-[#4A0E17]">
                                                        {s.name}
                                                    </h4>
                                                    <p className="mt-1 flex items-center gap-1 text-xs font-medium text-[#8A8178]">
                                                        <Clock className="size-3.5" />
                                                        {s.duration_min} min
                                                    </p>
                                                    {s.description && (
                                                        <p className="mt-2 line-clamp-2 text-sm text-[#5C534C]">
                                                            {s.description}
                                                        </p>
                                                    )}
                                                </div>
                                                <span className="shrink-0 font-serif text-2xl font-semibold text-[#C84B31]">
                                                    {inr(s.price)}
                                                </span>
                                            </div>
                                        </SalonCard>
                                    </button>
                                ))}
                            </div>
                            {filteredServices.length === 0 && (
                                <SalonCard className="p-10 text-center">
                                    <p className="text-sm font-medium text-[#8A8178]">
                                        No services available in this category yet.
                                    </p>
                                </SalonCard>
                            )}
                        </div>
                    </div>
                )}

                {/* Step 2 — time */}
                {step === 2 && selectedService && (
                    <div className="space-y-6" data-testid="booking-step-2">
                        <SalonCard className="flex flex-wrap items-center justify-between gap-4 p-5">
                            <div>
                                <p className={EYEBROW} style={{ color: "#C9A227" }}>Selected</p>
                                <p className="mt-1 font-serif text-2xl font-semibold text-[#4A0E17]">
                                    {selectedService.name}
                                </p>
                                <p className="mt-1 text-sm font-medium text-[#8A8178]">
                                    {selectedService.duration_min} min · {inr(selectedService.price)}
                                </p>
                            </div>
                            <Button
                                type="button"
                                variant="outline"
                                className={navBtn}
                                onClick={() => setStep(1)}
                            >
                                Change service
                            </Button>
                        </SalonCard>

                        <div className="grid gap-4 sm:grid-cols-2">
                            <div className="space-y-2">
                                <Label className={labelClass}>Stylist (optional)</Label>
                                <Select
                                    value={selectedStaff || ANY_STAFF}
                                    onValueChange={(v) => setSelectedStaff(v === ANY_STAFF ? "" : v)}
                                >
                                    <SelectTrigger className={fieldClass} data-testid="select-staff">
                                        <SelectValue placeholder="Any available stylist" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value={ANY_STAFF}>Any available stylist</SelectItem>
                                        {staff.map((s) => (
                                            <SelectItem key={s.id} value={s.id}>
                                                {s.full_name} · {s.role}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                            <div className="space-y-2">
                                <Label className={labelClass}>Date</Label>
                                <Input
                                    type="date"
                                    value={date}
                                    onChange={(e) => setDate(e.target.value)}
                                    min={new Date().toISOString().slice(0, 10)}
                                    className={fieldClass}
                                    data-testid="select-date"
                                />
                            </div>
                        </div>

                        <div>
                            <Label className={labelClass}>Available time slots</Label>
                            {slotsLoading ? (
                                <p className="mt-3 text-sm font-medium text-[#8A8178]">Loading slots…</p>
                            ) : (
                                <div className="mt-3 grid grid-cols-3 gap-2 sm:grid-cols-4 md:grid-cols-6">
                                    {slots.map((s) => {
                                        const time = formatSlotTime(s.start);
                                        const picked = selectedSlot === s.start;
                                        return (
                                            <button
                                                key={s.start}
                                                type="button"
                                                disabled={!s.available}
                                                onClick={() => setSelectedSlot(s.start)}
                                                className={`rounded-lg border px-2 py-2.5 text-sm font-semibold transition-all ${
                                                    !s.available
                                                        ? "cursor-not-allowed border-[#E8E4DE] bg-[#FAF9F6] text-[#C4BCB4] line-through"
                                                        : picked
                                                        ? "border-[#4A0E17] bg-[#4A0E17] text-white shadow-sm"
                                                        : "border-[#E8E4DE] bg-white text-[#3D2817] hover:border-[#D4AF37]/55 hover:bg-[#FFFEF9]"
                                                }`}
                                                data-testid={`slot-${time}`}
                                            >
                                                {time}
                                            </button>
                                        );
                                    })}
                                </div>
                            )}
                            {!slotsLoading && slots.length === 0 && (
                                <p className="mt-3 text-sm font-medium text-[#8A8178]">
                                    No slots for this date — try another day or stylist.
                                </p>
                            )}
                        </div>

                        <Button
                            type="button"
                            onClick={() => setStep(3)}
                            disabled={!selectedSlot}
                            className={primaryBtn}
                            data-testid="next-step-button"
                        >
                            Continue
                            <ChevronRight className="ml-1 size-4" />
                        </Button>
                    </div>
                )}

                {/* Step 3 — details */}
                {step === 3 && selectedService && (
                    <div className="grid gap-8 md:grid-cols-3" data-testid="booking-step-3">
                        <div className="space-y-4 md:col-span-2">
                            <div>
                                <p className={EYEBROW} style={{ color: "#C9A227" }}>Almost done</p>
                                <h3 className="mt-1 font-serif text-2xl font-semibold text-[#4A0E17]">
                                    Your details
                                </h3>
                            </div>

                            <div className="space-y-2">
                                <Label className={labelClass}>Full name</Label>
                                <div className="relative">
                                    <User className="pointer-events-none absolute left-4 top-1/2 size-4 -translate-y-1/2 text-[#8A8178]" />
                                    <Input
                                        value={form.customer_name}
                                        onChange={(e) => setForm({ ...form, customer_name: e.target.value })}
                                        className={`${fieldClass} pl-11`}
                                        placeholder="Your full name"
                                        data-testid="cust-name-input"
                                    />
                                </div>
                            </div>

                            <div className="grid gap-4 sm:grid-cols-2">
                                <div className="space-y-2">
                                    <Label className={labelClass}>Mobile</Label>
                                    <div className="relative">
                                        <Phone className="pointer-events-none absolute left-4 top-1/2 size-4 -translate-y-1/2 text-[#8A8178]" />
                                        <Input
                                            value={form.mobile}
                                            onChange={(e) => setForm({ ...form, mobile: e.target.value })}
                                            placeholder="+91 98765 43210"
                                            className={`${fieldClass} pl-11`}
                                            data-testid="cust-mobile-input"
                                        />
                                    </div>
                                </div>
                                <div className="space-y-2">
                                    <Label className={labelClass}>Email (optional)</Label>
                                    <div className="relative">
                                        <Mail className="pointer-events-none absolute left-4 top-1/2 size-4 -translate-y-1/2 text-[#8A8178]" />
                                        <Input
                                            type="email"
                                            value={form.email}
                                            onChange={(e) => setForm({ ...form, email: e.target.value })}
                                            className={`${fieldClass} pl-11`}
                                            placeholder="you@email.com"
                                        />
                                    </div>
                                </div>
                            </div>

                            <div className="space-y-2">
                                <Label className={labelClass}>Notes (optional)</Label>
                                <Input
                                    value={form.notes}
                                    onChange={(e) => setForm({ ...form, notes: e.target.value })}
                                    className={fieldClass}
                                    placeholder="Allergies, preferences…"
                                />
                            </div>

                            <div className="flex flex-wrap gap-3">
                                <Button
                                    type="button"
                                    variant="outline"
                                    className={navBtn}
                                    onClick={() => setStep(2)}
                                >
                                    Back
                                </Button>
                                <Button
                                    type="button"
                                    className={primaryBtn}
                                    disabled={submitting}
                                    onClick={submitBooking}
                                    data-testid="confirm-booking-button"
                                >
                                    {submitting ? "Confirming…" : "Confirm booking"}
                                </Button>
                            </div>
                        </div>

                        <aside className="md:col-span-1">
                            <SalonCard className="sticky top-4 p-5">
                                <p className={EYEBROW} style={{ color: "#C9A227" }}>Summary</p>
                                <ul className="mt-4 space-y-2.5 text-sm font-medium">
                                    <li className="flex justify-between gap-2">
                                        <span className="text-[#8A8178]">Service</span>
                                        <span className="text-right text-[#2D2926]">{selectedService.name}</span>
                                    </li>
                                    <li className="flex justify-between gap-2">
                                        <span className="text-[#8A8178]">Duration</span>
                                        <span>{selectedService.duration_min} min</span>
                                    </li>
                                    <li className="flex justify-between gap-2">
                                        <span className="text-[#8A8178]">When</span>
                                        <span className="text-right">{formatDateTime(selectedSlot)}</span>
                                    </li>
                                    <li className="flex justify-between gap-2 border-t border-[#E8E4DE] pt-3 font-serif text-lg font-semibold text-[#4A0E17]">
                                        <span>Total</span>
                                        <span className="text-[#C84B31]">{inr(selectedService.price)}</span>
                                    </li>
                                </ul>
                            </SalonCard>
                        </aside>
                    </div>
                )}

                {/* Step 4 — confirmation */}
                {step === 4 && confirmation && (
                    <SalonCard className="mx-auto max-w-xl p-10 text-center" data-testid="booking-confirmation">
                        <div className="mx-auto mb-6 flex size-16 items-center justify-center rounded-full bg-[#FDF3DC]">
                            <Sparkles className="size-8 text-[#D4AF37]" />
                        </div>
                        <p className={EYEBROW} style={{ color: "#C9A227" }}>Confirmed</p>
                        <h2 className="mt-2 font-serif text-3xl font-semibold text-[#4A0E17] sm:text-4xl">
                            You&apos;re booked!
                        </h2>
                        <GoldDivider className="mx-auto mt-4" />
                        <p className="mx-auto mt-5 max-w-md text-sm font-medium leading-relaxed text-[#5C534C]">
                            <strong className="text-[#4A0E17]">{confirmation.service_name}</strong>
                            {" "}at <strong className="text-[#4A0E17]">{tenant.name}</strong>
                            <br />
                            <span className="mt-2 inline-flex items-center justify-center gap-1.5 text-[#8A8178]">
                                <CalendarDays className="size-4 text-[#D4AF37]" />
                                {formatDateTime(confirmation.appointment.start_at)}
                            </span>
                        </p>
                        <p className="mt-4 text-xs font-medium text-[#8A8178]">
                            We&apos;ll reach you at <strong className="text-[#3D2817]">{form.mobile}</strong>
                        </p>
                    </SalonCard>
                )}
            </main>

            <footer className="border-t border-[#E8E4DE] bg-white/80 py-5 text-center">
                <p className="flex items-center justify-center gap-2 text-xs font-medium text-[#8A8178]">
                    Powered by
                    <BrandLogo
                        size={22}
                        textClass="font-serif text-sm font-semibold text-[#4A0E17]"
                        accentClass="text-[9px] font-semibold uppercase tracking-[0.16em] text-[#C84B31]"
                    />
                </p>
            </footer>

            <AIChatWidget slug={slug} salonName={tenant.name} />
        </div>
    );
}

function AIChatWidget({ slug, salonName }) {
    const [open, setOpen] = useState(false);
    const [msgs, setMsgs] = useState([
        {
            role: "assistant",
            content: `Namaste! I'm Aaria, your booking assistant for ${salonName}. Ask about services, timings, or help booking. ✨`,
        },
    ]);
    const [text, setText] = useState("");
    const [busy, setBusy] = useState(false);
    const sessionRef = useRef(`pub-${Math.random().toString(36).slice(2)}`);
    const scrollRef = useRef(null);

    const send = async () => {
        if (!text.trim() || busy) return;
        const userMsg = { role: "user", content: text.trim() };
        setMsgs((m) => [...m, userMsg]);
        setText("");
        setBusy(true);
        try {
            const r = await api.post("/ai/chat", {
                session_id: sessionRef.current,
                text: userMsg.content,
                tenant_slug: slug,
            });
            setMsgs((m) => [...m, { role: "assistant", content: r.data.reply }]);
        } catch {
            setMsgs((m) => [
                ...m,
                { role: "assistant", content: "Sorry, I'm having trouble. Please try again in a moment." },
            ]);
        } finally {
            setBusy(false);
            setTimeout(() => scrollRef.current?.scrollTo({ top: 9999, behavior: "smooth" }), 50);
        }
    };

    return (
        <>
            <button
                type="button"
                onClick={() => setOpen(!open)}
                className="fixed bottom-6 right-6 z-40 flex size-14 items-center justify-center rounded-full bg-[#4A0E17] text-white shadow-[0_8px_28px_rgba(74,14,23,0.35)] transition-transform hover:scale-105"
                data-testid="ai-chat-toggle"
            >
                {open ? <X className="size-6" /> : <MessageCircle className="size-6" />}
            </button>

            {open && (
                <div
                    className="fixed bottom-24 right-6 z-40 flex h-[min(520px,calc(100vh-120px))] w-[360px] max-w-[calc(100vw-32px)] flex-col overflow-hidden rounded-2xl border border-[#E8E4DE] bg-white shadow-[0_16px_48px_rgba(45,41,38,0.15)]"
                    data-testid="ai-chat-window"
                >
                    <div className="flex items-center gap-3 border-b border-[#E8E4DE] bg-[#4A0E17] px-4 py-3 text-white">
                        <img src={AI_AVATAR} alt="" className="size-9 rounded-full bg-[#D4AF37]/20 object-cover" />
                        <div>
                            <div className="font-serif text-lg leading-tight">Aaria</div>
                            <div className="text-xs text-white/75">Your booking concierge</div>
                        </div>
                    </div>

                    <div ref={scrollRef} className="scrollbar-salon flex-1 space-y-3 overflow-y-auto p-3">
                        {msgs.map((m, i) => (
                            <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                                <div
                                    className={`max-w-[85%] whitespace-pre-wrap rounded-xl px-3 py-2 text-sm ${
                                        m.role === "user"
                                            ? "rounded-br-sm bg-[#4A0E17] text-white"
                                            : "rounded-bl-sm bg-[#FAF9F6] text-[#2D2926]"
                                    }`}
                                >
                                    {m.content}
                                </div>
                            </div>
                        ))}
                        {busy && (
                            <div className="flex justify-start">
                                <div className="rounded-xl bg-[#FAF9F6] px-3 py-2 text-sm text-[#8A8178]">
                                    Aaria is typing…
                                </div>
                            </div>
                        )}
                    </div>

                    <form
                        onSubmit={(e) => { e.preventDefault(); send(); }}
                        className="flex gap-2 border-t border-[#E8E4DE] p-2"
                    >
                        <Input
                            value={text}
                            onChange={(e) => setText(e.target.value)}
                            placeholder="Ask Aaria…"
                            className={`${fieldClass} h-10`}
                            data-testid="chat-input"
                        />
                        <Button
                            type="submit"
                            size="icon"
                            disabled={busy}
                            className={`${primaryBtn} size-10 shrink-0`}
                            data-testid="chat-send"
                        >
                            <Send className="size-4" />
                        </Button>
                    </form>
                </div>
            )}
        </>
    );
}
